import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { MIN_SCORED_CREATIVES_FOR_INSIGHTS } from "@nexus/contracts";
import type { PreflightReportDetail } from "../../types/preflight.types";
import { scoredAssetCount } from "../../lib/report-blockers";
import {
  buildCriteriaMatrix,
  scoredMatrixAssets,
} from "../../lib/report-matrix";
import {
  computeScoreAttribution,
  hasScoreComposition,
} from "../../lib/report-attribution";
import { computeReportFindings } from "../../lib/report-findings";
import { ScoreComposition } from "../ScoreComposition/ScoreComposition";
import { ReportFindings } from "../ReportFindings/ReportFindings";
import { ScoreRail } from "../ScoreRail/ScoreRail";
import { CriteriaHeatmap } from "../CriteriaHeatmap/CriteriaHeatmap";
import { InsightTabs } from "../InsightTabs/InsightTabs";
import type { InsightTab } from "../InsightTabs/InsightTabs";

/**
 * Cross-creative synthesis only earns its space when there's more than one creative to
 * compare. Re-exported so the market dashboard gates on the same threshold (R26).
 */
export const MIN_SCORED_ASSETS = MIN_SCORED_CREATIVES_FOR_INSIGHTS;

export interface ReportInsightsProps {
  report: PreflightReportDetail;
  /** Jump to a creative's card (score dot / heatmap column). */
  onSelectAsset?: (assetId: string) => void;
  /** Jump to a specific criterion row inside a creative's card (heatmap cell). */
  onSelectCriterion?: (assetId: string, criterionKey: string) => void;
  className?: string;
}

/**
 * Consolidates the report-level visualizations (score spread, top blockers, findings, criteria
 * heatmap) into a single tabbed card — one panel at a time keeps the page calm instead of
 * stacking several open sections. Tabs appear only when their data exists; the whole card hides
 * for single-creative reports where the per-card breakdown already suffices.
 *
 * This is the only report-shaped entry point into those views: it adapts one report into the
 * data each view renders, which the market dashboard feeds from the aggregate response instead.
 */
export function ReportInsights({
  report,
  onSelectAsset,
  onSelectCriterion,
  className,
}: ReportInsightsProps) {
  const { t } = useTranslation();
  const scored = scoredAssetCount(report);

  const tabs = useMemo<InsightTab[]>(() => {
    if (scored < MIN_SCORED_ASSETS) return [];

    const fallback = t("preflight.creativeCard.fallbackName");
    const assets = scoredMatrixAssets(report, fallback);
    const matrix = buildCriteriaMatrix(report, fallback);

    const list: InsightTab[] = [
      {
        value: "spread",
        label: t("preflight.rail.title"),
        badge: scored,
        help: "spread",
        content: (
          <ScoreRail assets={assets} onSelectAsset={onSelectAsset} headless />
        ),
      },
    ];

    // One list, not two: the blocker ranking and the score attribution rank the same criteria by
    // the same thing — one as a heuristic, one in points — so they share a single tab where the
    // points quantify the ranking and selection projects what fixing it is worth. The aggregate
    // dashboard still renders `ReportTopBlockers`, which has no per-creative scores to attribute.
    const attribution = computeScoreAttribution(report);
    if (hasScoreComposition(attribution)) {
      list.push({
        value: "blockers",
        label: t("preflight.blockers.title"),
        badge: attribution.rows.filter((row) => !row.isTail).length,
        help: "composition",
        content: <ScoreComposition report={report} headless />,
      });
    }

    // Evidence follows the ranking: the blocker list names the rules, this names what the
    // analyzer saw. Absent whenever DS emitted no sub-check details.
    const findings = computeReportFindings(report);
    if (findings.length > 0) {
      list.push({
        value: "findings",
        label: t("preflight.findings.title"),
        badge: findings.length,
        help: "findings",
        content: <ReportFindings report={report} headless />,
      });
    }

    if (matrix.rows.length > 0) {
      list.push({
        value: "heatmap",
        label: t("preflight.heatmap.title"),
        badge: matrix.rows.length,
        help: "heatmap",
        content: (
          <CriteriaHeatmap
            matrix={matrix}
            scoredCount={scored}
            onSelectColumn={onSelectAsset}
            onSelectCriterion={onSelectCriterion}
            headless
          />
        ),
      });
    }

    return list;
  }, [report, scored, onSelectAsset, onSelectCriterion, t]);

  if (tabs.length === 0) return null;

  return <InsightTabs tabs={tabs} className={className} />;
}
